import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { supabase } from './services/db';
import { getCurrentUser } from './services/authService';
import { User } from './types';

// Components
import ScrollToTop from './components/ScrollToTop';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';

// Pages
import HomePage from './pages/HomePage';
import RegistrationPage from './pages/RegistrationPage';
import LoginPage from './pages/LoginPage';
import CourseAccessPage from './pages/CourseAccessPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminTransactionsPage from './pages/AdminTransactionsPage';
import AdminTestsPage from './pages/AdminTestsPage';
import AdminVouchersPage from './pages/AdminVouchersPage';
import AdminVoucherReportPage from './pages/AdminVoucherReportPage';
import AdminLoginAttemptsPage from './pages/AdminLoginAttemptsPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import InviteSignupPage from './pages/InviteSignupPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ImprintPage from './pages/ImprintPage';
import TestPage from './pages/TestPage';
import CertificatePage from './pages/CertificatePage';
import TermsPage from './pages/TermsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import FAQPage from './pages/FAQPage';
import CertificateVerificationPage from './pages/CertificateVerificationPage';
import PaymentRequiredPage from './pages/PaymentRequiredPage';
import NotFoundPage from './pages/NotFoundPage';

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Payment Required Route Component
const PaymentRequiredRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, setUser } = useAuth();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasRefreshed, setHasRefreshed] = useState(false);

  useEffect(() => {
    const refreshUserData = async () => {
      if (user?.isDisabled && !hasRefreshed && !isRefreshing) {
        setIsRefreshing(true);

        try {
          const { data: { user: latestUser }, error: getUserError } = await supabase.auth.getUser();

          if (!getUserError && latestUser) {
            const freshUser = await getCurrentUser(latestUser);
            if (freshUser) {
              setUser(freshUser);
            }
          }
        } catch (error) {
          console.error('Error refreshing user data:', error);
        } finally {
          setIsRefreshing(false);
          setHasRefreshed(true);
        }
      }
    };

    refreshUserData();
  }, [user?.isDisabled, hasRefreshed, isRefreshing, setUser]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isRefreshing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Aktualisiere Kontostatus...</p>
        </div>
      </div>
    );
  }

  if (user?.isDisabled) {
    return <Navigate to="/payment-required" replace />;
  }

  return <>{children}</>;
};

// Admin Route Component
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated } = useAuth();
  
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  
  const isAdmin = user.isAdmin === true || user.isSuperAdmin === true;
  
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
    <Router>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen">
        <Navbar />
        
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage />} />
            
            <Route path="/registrieren" element={<RegistrationPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/invite-signup" element={<InviteSignupPage />} />
            <Route path="/datenschutz" element={<PrivacyPolicyPage />} />
            <Route path="/impressum" element={<ImprintPage />} />
            <Route path="/agb" element={<TermsPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/payment-required" element={<PaymentRequiredPage />} />

            {/* Certificate Verification - Public Route */}
            <Route path="/verify/:certNumber" element={<CertificateVerificationPage />} />

            {/* Protected Routes */}
            <Route
              path="/kurszugang"
              element={
                <PaymentRequiredRoute>
                  <CourseAccessPage />
                </PaymentRequiredRoute>
              }
            />
            <Route
              path="/test"
              element={
                <PaymentRequiredRoute>
                  <TestPage />
                </PaymentRequiredRoute>
              }
            />
            <Route
              path="/zertifikat"
              element={
                <PaymentRequiredRoute>
                  <CertificatePage />
                </PaymentRequiredRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route 
              path="/admin" 
              element={
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              } 
            />
            <Route
              path="/admin/vouchers"
              element={
                <AdminRoute>
                  <AdminVouchersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/vouchers/:voucherId/report"
              element={
                <AdminRoute>
                  <AdminVoucherReportPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users" 
              element={
                <AdminRoute>
                  <AdminUsersPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/tests" 
              element={
                <AdminRoute>
                  <AdminTestsPage />
                </AdminRoute>
              } 
            />
            <Route
              path="/admin/transactions"
              element={
                <AdminRoute>
                  <AdminTransactionsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/login-attempts"
              element={
                <AdminRoute>
                  <AdminLoginAttemptsPage />
                </AdminRoute>
              }
            />

            {/* 404 Not Found Page */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        
        <Footer />
        <CookieConsent />
      </div>
    </Router>
    </AuthProvider>
  );
};

export default App;